/**
 * reverenceAndToneBalancer.js
 *
 * Detects and corrects imbalances between reverence and warmth.
 *
 * Two drift patterns to guard against:
 *   over_solemn  — spiritual overlay on operational questions; cold formality
 *   over_casual  — insufficient reverence for prayer, grief, sacred themes
 *
 * DISTINCT from empathyBalanceEngine (FASE 6) which handles empathy ↔ clarity.
 * This engine handles the vertical dimension: sacred ↔ ordinary.
 */

/**
 * @param {{
 *   intent:             string,
 *   emotion:            object|null,
 *   mood:               string,
 *   wisdomTone:         string,
 *   conversationLength: number,
 * }} params
 * @returns {{ reverenceLevel: "low"|"medium"|"high", instruction: string|null }}
 */
export function balanceReverenceAndTone({ intent, emotion, mood, wisdomTone, conversationLength }) {
  const isOperational  = intent === "planning" || intent === "analysis" || intent === "events";
  const isSacredTopic  = intent === "prayer" || intent === "sermon" ||
                         intent === "reflection" ||
                         emotion?.dominantEmotion === "grief" ||
                         emotion?.dominantEmotion === "spiritual_confusion";

  // Over-solemnity risk: operational question receiving a contemplative/reflective tone
  const isOverSolemn =
    isOperational &&
    (wisdomTone === "contemplativo" || wisdomTone === "reflexivo");

  if (isOverSolemn) {
    return {
      reverenceLevel: "low",
      instruction:
        "Evita overlay espiritual innecesario en preguntas operacionales. Responde con practicidad pastoral.",
    };
  }

  // Under-reverence risk: sacred topic receiving a purely practical tone
  const isUnderReverent = isSacredTopic && wisdomTone === "practico";

  if (isUnderReverent) {
    return {
      reverenceLevel: "high",
      instruction: "Mantén reverencia apropiada para el tema. No lo trates como consulta operacional.",
    };
  }

  // Prayer / grief / spiritual reflection → high reverence is fitting
  if (isSacredTopic) {
    return { reverenceLevel: "high", instruction: null };
  }

  // Operational → low reverence; no extra instruction needed
  if (isOperational) {
    return { reverenceLevel: "low", instruction: null };
  }

  return { reverenceLevel: "medium", instruction: null };
}
